import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown, 
  History,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Target
} from 'lucide-react';
import { formatNumber, formatCurrency } from '../utils/format';

// Mock portfolio data
const portfolioData = {
  totalValue: 12580.50,
  totalProfit: 2345.80,
  profitPercent: 22.9,
  availableBalance: 5000.00,
  invested: 7580.50,
};

const positions = [
  {
    id: '1',
    market: 'Will Bitcoin reach $100K by end of 2024?',
    outcome: 'Yes',
    invested: 2500,
    currentValue: 3250,
    pnl: 750,
    pnlPercent: 30,
    endDate: '2024-12-31',
  },
  {
    id: '2',
    market: 'Will ETH ETF be approved by SEC in 2024?',
    outcome: 'Yes',
    invested: 1500,
    currentValue: 2100,
    pnl: 600,
    pnlPercent: 40,
    endDate: '2024-12-31',
  },
  {
    id: '3',
    market: 'Will OpenAI release GPT-5 before July 2024?',
    outcome: 'No',
    invested: 1000,
    currentValue: 850,
    pnl: -150,
    pnlPercent: -15,
    endDate: '2024-06-30',
  },
];

const tradeHistory = [
  { id: '1', market: 'Bitcoin $100K', type: 'buy', outcome: 'Yes', amount: 1000, date: '2024-03-15' },
  { id: '2', market: 'ETH ETF Approval', type: 'buy', outcome: 'Yes', amount: 500, date: '2024-03-14' },
  { id: '3', market: 'SpaceX Starship', type: 'sell', outcome: 'Yes', amount: 300, date: '2024-03-12' },
  { id: '4', market: 'Trump Election', type: 'buy', outcome: 'No', amount: 800, date: '2024-03-10' },
];

export function Portfolio() {
  const [activeTab, setActiveTab] = useState<'overview' | 'positions' | 'history'>('overview');

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 via-purple-600/10 to-pink-600/10" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-bold text-white mb-2">Portfolio</h1>
            <p className="text-gray-400">Track your positions and trading history</p>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-indigo-400" />
                </div>
                <span className="text-sm text-gray-400">Total Value</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatCurrency(portfolioData.totalValue)}</p>
            </div>

            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
                <span className="text-sm text-gray-400">Total Profit</span>
              </div>
              <p className={`text-2xl font-bold ${portfolioData.totalProfit >= 0 ? 'text-omni-success' : 'text-omni-danger'}`}>
                {portfolioData.totalProfit >= 0 ? '+' : ''}{formatCurrency(portfolioData.totalProfit)}
              </p>
              <p className={`text-sm ${portfolioData.profitPercent >= 0 ? 'text-omni-success' : 'text-omni-danger'}`}>
                {portfolioData.profitPercent >= 0 ? '+' : ''}{portfolioData.profitPercent}%
              </p>
            </div>

            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <PieChart className="w-5 h-5 text-purple-400" />
                </div>
                <span className="text-sm text-gray-400">Invested</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatCurrency(portfolioData.invested)}</p>
            </div>

            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-sm text-gray-400">Available</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatCurrency(portfolioData.availableBalance)}</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-2 border-b border-omni-border">
          {[
            { id: 'overview', label: 'Overview', icon: PieChart },
            { id: 'positions', label: 'Positions', icon: Target },
            { id: 'history', label: 'History', icon: History },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors relative ${
                activeTab === tab.id ? 'text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {activeTab === tab.id && (
                <motion.div
                  layoutId="tab-indicator"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500"
                />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="py-8">
          {activeTab === 'overview' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* Recent Positions */}
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-white">Active Positions</h3>
                  <button
                    onClick={() => setActiveTab('positions')}
                    className="text-sm text-indigo-400 hover:text-indigo-300"
                  >
                    View All
                  </button>
                </div>

                <div className="space-y-4">
                  {positions.slice(0, 3).map((position) => (
                    <div key={position.id} className="flex items-center justify-between p-4 bg-omni-card rounded-xl">
                      <div className="flex-1">
                        <p className="font-medium text-white">{position.market}</p>
                        <p className="text-sm text-gray-500">{position.outcome} • Ends {position.endDate}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-white">{formatCurrency(position.currentValue)}</p>
                        <p className={`text-sm ${position.pnl >= 0 ? 'text-omni-success' : 'text-omni-danger'}`}>
                          {position.pnl >= 0 ? '+' : ''}{formatCurrency(position.pnl)} ({position.pnlPercent}%)
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Activity */}
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-white">Recent Activity</h3>
                  <button
                    onClick={() => setActiveTab('history')}
                    className="text-sm text-indigo-400 hover:text-indigo-300"
                  >
                    View All
                  </button>
                </div>

                <div className="space-y-3">
                  {tradeHistory.slice(0, 4).map((trade) => (
                    <div key={trade.id} className="flex items-center justify-between py-3 border-b border-omni-border last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          trade.type === 'buy' ? 'bg-omni-success/10' : 'bg-omni-danger/10'
                        }`}>
                          {trade.type === 'buy' ? <ArrowUpRight className="w-4 h-4 text-omni-success" /> : <ArrowDownRight className="w-4 h-4 text-omni-danger" />}
                        </div>
                        <div>
                          <p className="text-sm text-white">{trade.type === 'buy' ? 'Bought' : 'Sold'} {trade.outcome}</p>
                          <p className="text-xs text-gray-500">{trade.market}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-white">{formatCurrency(trade.amount)}</p>
                        <p className="text-xs text-gray-500">{trade.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'positions' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="glass rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-omni-card">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-gray-400">Market</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-400">Outcome</th>
                      <th className="text-right p-4 text-sm font-medium text-gray-400">Invested</th>
                      <th className="text-right p-4 text-sm font-medium text-gray-400">Current</th>
                      <th className="text-right p-4 text-sm font-medium text-gray-400">P&L</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-400">Ends</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map((position) => (
                      <tr key={position.id} className="border-t border-omni-border">
                        <td className="p-4">
                          <p className="font-medium text-white">{position.market}</p>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-400">
                            {position.outcome}
                          </span>
                        </td>
                        <td className="p-4 text-right text-white">{formatCurrency(position.invested)}</td>
                        <td className="p-4 text-right text-white">{formatCurrency(position.currentValue)}</td>
                        <td className="p-4 text-right">
                          <span className={position.pnl >= 0 ? 'text-omni-success' : 'text-omni-danger'}>
                            {position.pnl >= 0 ? '+' : ''}{formatCurrency(position.pnl)}
                          </span>
                        </td>
                        <td className="p-4 text-gray-400">{position.endDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {activeTab === 'history' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              {tradeHistory.map((trade) => (
                <div key={trade.id} className="glass rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      trade.type === 'buy' ? 'bg-omni-success/10' : 'bg-omni-danger/10'
                    }`}>
                      {trade.type === 'buy' ? <ArrowUpRight className="w-5 h-5 text-omni-success" /> : <ArrowDownRight className="w-5 h-5 text-omni-danger" />}
                    </div>
                    <div>
                      <p className="font-medium text-white">{trade.type === 'buy' ? 'Bought' : 'Sold'} {trade.outcome}</p>
                      <p className="text-sm text-gray-500">{trade.market}</p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="font-semibold text-white">{formatCurrency(trade.amount)}</p>
                    <p className="text-sm text-gray-500">{trade.date}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
